import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import ActionMenu from 'components/partials/action-menu/action-menu';
import { Typography } from 'ui/typography';
import Button from 'components/partials/button/button';

import Icons from 'assets/icons';

import type { ActionMenuItem } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal.helpers';

const HEADER_SX: SxProps<Theme> = {
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  minHeight: '2.75rem',
};

const getDetailsTitle = (isNew: boolean, isEditMode: boolean): string => {
  const goalStatuses = {
    new: 'New Goal',
    edit: 'Edit Goal',
    read: 'Goal Details',
  };

  return (
    (isNew && goalStatuses.new) ||
    (isEditMode && goalStatuses.edit) ||
    goalStatuses.read
  );
};

const getHeaderTitle = (
  isNew: boolean,
  isEditMode: boolean,
  activeTab: string
): string | undefined => {
  const TABS: Record<string, string> = {
    details: getDetailsTitle(isNew, isEditMode),
    history: 'Version History',
    criticalElements: 'Linked Critical Elements',
  };

  return TABS[activeTab];
};

interface GoalHeaderProps {
  actionMenuItems: ActionMenuItem[];
  onEditClick: () => void;
  sx?: SxProps<Theme>;
  isNew: boolean;
  isEditMode: boolean;
  onCancel: (isNew: boolean) => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  activeTab: string;
  onMonitoringClick: () => void;
  shouldShowMonitoring: boolean;
}

const GoalHeader = ({
  actionMenuItems,
  onEditClick,
  sx,
  isNew,
  isEditMode,
  onCancel,
  onSubmit,
  activeTab,
  onMonitoringClick,
  shouldShowMonitoring,
}: GoalHeaderProps): React.JSX.Element => (
  <Stack direction="row" sx={mergeSx(HEADER_SX, sx)}>
    <Typography
      variant="h3"
      sx={{ textTransform: 'capitalize' }}
      data-testid="goal-header-title"
    >
      {getHeaderTitle(isNew, isEditMode, activeTab)}
    </Typography>

    {activeTab === 'details' &&
      (isNew || isEditMode ? (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Button
            variant="reserve-invert"
            onClick={() => onCancel(isNew)}
            data-testid="cancel-goal-header-button"
          >
            Cancel
          </Button>
          <Button
            variant="brand-filled"
            onClick={onSubmit}
            data-testid="save-goal-header-button"
          >
            Save changes
          </Button>
        </Stack>
      ) : (
        <ActionMenu
          buttonName="Edit goal"
          options={actionMenuItems}
          onEditClick={onEditClick}
        />
      ))}

    {activeTab === 'criticalElements' &&
      !isNew &&
      !isEditMode &&
      shouldShowMonitoring && (
        <Button
          variant="reserve-outlined"
          onClick={onMonitoringClick}
          iconBefore={<Icons.Steps aria-label="StepsIcon" />}
        >
          Monitoring details
        </Button>
      )}
  </Stack>
);

export default GoalHeader;
